// src/components/reviewerdash/PendingReviews.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Clock, 
  Eye, 
  Calendar,
  Search,
  FileText,
  AlertCircle,
  Download,
  Play,
  Loader
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import axios from "../../utils/axios";
import { showToast } from '../../utils/toast';
import RichTextViewer from '../common/RichTextViewer';
import { handleDownloadFile } from '../../utils/utils.global';

// Enhanced parseQuillContent function to handle array format
const parseQuillContent = (content) => {
  if (!content) return '';

  try {
    // If it's already a string with HTML, return as is
    if (typeof content === 'string' && (content.includes('<p>') || content.includes('<div>'))) {
      return content;
    }

    // If it's an array of operations (like your email body format)
    if (Array.isArray(content)) {
      let html = '';
      content.forEach(op => {
        let text = op.insert || '';
        if (op.attributes) {
          if (op.attributes.bold) text = `<strong>${text}</strong>`;
          if (op.attributes.italic) text = `<em>${text}</em>`;
          if (op.attributes.underline) text = `<u>${text}</u>`;
          if (op.attributes.link) text = `<a href="${op.attributes.link}" target="_blank">${text}</a>`;
          if (op.attributes.color) {
            text = `<span style="color: ${op.attributes.color}">${text}</span>`;
          }
          if (op.attributes.header) {
            const level = op.attributes.header;
            text = `<h${level}>${text}</h${level}>`;
          }
          if (op.attributes.list) {
            if (op.attributes.list === 'bullet') {
              text = `<li>${text}</li>`;
            } else if (op.attributes.list === 'ordered') {
              text = `<li>${text}</li>`;
            }
          }
        }
        html += text;
      });

      // Wrap lists if needed
      if (html.includes('<li>')) {
        html = `<ul>${html}</ul>`;
      }

      // Convert newlines to <br> tags for proper formatting
      html = html.replace(/\n/g, '<br>');

      return html;
    }

    // If it's a Quill Delta object (like {"ops": [...]})
    if (typeof content === 'object' && content.ops) {
      return parseQuillContent(content.ops);
    }

    // If it's a JSON string
    if (typeof content === 'string') {
      try {
        const parsed = JSON.parse(content);
        return parseQuillContent(parsed);
      } catch (e) {
        // Not JSON, return as is
        return content;
      }
    }

    return content;
  } catch (error) {
    console.error('Error parsing Quill content:', error);
    return content;
  }
};

const PendingReviews = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [stats, setStats] = useState({
    total: 0,
    urgent: 0,
    high: 0,
    dueThisWeek: 0
  });
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    limit: 10
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch pending reviews
  useEffect(() => {
    const fetchPendingReviews = async () => {
      setLoading(true);
      try {
        const response = await axios.get('/reviewer/pending-reviews', {
          params: {
            page: currentPage,
            limit: pagination.limit,
            search: debouncedSearchTerm,
            priority: filterPriority !== 'all' ? filterPriority : undefined
          }
        });

        if (response.data.success) {
          setReviews(response.data.reviews);
          setStats(response.data.stats);
          setPagination(response.data.pagination);
        }
      } catch (error) {
        console.error('Error fetching pending reviews:', error);
        showToast('Failed to load pending reviews. Please try again.', 'error');
      } finally {
        setLoading(false);
      }
    };

    fetchPendingReviews();
  }, [currentPage, pagination.limit, debouncedSearchTerm, filterPriority]);

  const getPriorityColor = (priority) => {
    const colors = {
      urgent: 'bg-red-100 text-red-800 border-red-200',
      high: 'bg-orange-100 text-orange-800 border-orange-200',
      normal: 'bg-blue-100 text-blue-800 border-blue-200',
      low: 'bg-gray-100 text-gray-600 border-gray-200'
    };
    return colors[priority] || colors.normal;
  };

  const getDaysLeftColor = (days) => {
    if (days <= 3) return 'text-red-600 font-bold';
    if (days <= 7) return 'text-orange-600 font-semibold';
    if (days <= 14) return 'text-yellow-600';
    return 'text-green-600';
  };

  const handleStartReview = (manuscriptId) => {
    navigate(`/reviewerdash/review/${manuscriptId}`);
  };

  const handleViewDetails = (review) => {
    setSelectedReview(review);
    setShowDetailsModal(true);
  };

  const handlePageChange = (page) => {
    if (page >= 1 && page <= pagination.totalPages) {
      setCurrentPage(page);
    }
  };

  if (loading && reviews.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-12 h-12 animate-spin text-[--asfi-blue] mx-auto mb-4" />
          <p className="text-gray-600">Loading pending reviews...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Page Header */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-[--asfi-blue] flex items-center">
              <Clock className="w-6 h-6 mr-3" />
              Reviews In-Progress
            </h1>
            <p className="text-gray-600 mt-2">Reviews awaiting your attention and feedback</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Pending</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                </div>
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <Clock className="w-5 h-5 text-[--asfi-blue]" />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Urgent</p>
                  <p className="text-2xl font-bold text-red-600">{stats.urgent}</p>
                </div>
                <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-red-600" />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">High Priority</p>
                  <p className="text-2xl font-bold text-orange-600">{stats.high}</p>
                </div>
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <AlertCircle className="w-5 h-5 text-orange-600" />
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Due This Week</p>
                  <p className="text-2xl font-bold text-yellow-600">{stats.dueThisWeek}</p>
                </div>
                <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-yellow-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Search and Filters */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              {/* Search */}
              <div className="md:col-span-3">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Search by title or manuscript ID..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Priority Filter */}
              <div>
                <select
                  value={filterPriority}
                  onChange={(e) => {
                    setFilterPriority(e.target.value);
                    setCurrentPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="all">All Priorities</option>
                  <option value="urgent">Urgent</option>
                  <option value="high">High</option>
                  <option value="normal">Normal</option>
                  <option value="low">Low</option>
                </select>
              </div>
            </div>
          </div>

          {/* Reviews In-Progress Table */}
          {reviews.length === 0 ? (
            <div className="bg-white rounded-xl shadow-md p-12 text-center">
              <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-700 mb-2">No Reviews In-Progress</h3>
              <p className="text-gray-500">You're all caught up! New review invitations will appear here.</p>
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-[--asfi-blue]">
                    <tr>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                        Priority
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                        Manuscript
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                        Invited
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                        Due Date
                      </th>
                      <th scope="col" className="px-6 py-4 text-left text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                        Days Left
                      </th>
                      <th scope="col" className="px-6 py-4 text-right text-xs font-medium text-[--asfi-purple] uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reviews.map((review) => (
                      <tr key={review.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getPriorityColor(review.priority)}`}>
                            {review.priority.charAt(0).toUpperCase() + review.priority.slice(1)}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{review.manuscriptId}</p>
                            <p className="text-sm text-gray-500 truncate max-w-xs">{review.title}</p>
                            <p className="text-xs text-gray-400 mt-1">
                              {review.authors?.slice(0, 2).join(', ')}
                              {review.authors?.length > 2 && ' et al.'}
                            </p>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {review.invitedDate}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center text-sm text-gray-500">
                            <Calendar className="w-4 h-4 mr-2 text-gray-400" />
                            {review.dueDate}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`text-sm font-medium ${getDaysLeftColor(review.daysLeft)}`}>
                            {review.daysLeft} days
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <button
                              onClick={() => handleViewDetails(review)}
                              className="p-2 text-gray-600 hover:text-[--asfi-blue] hover:bg-purple-50 rounded-lg transition-colors"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleStartReview(review.manuscriptId)}
                              className="px-3 py-1.5 bg-[--asfi-blue] text-white text-xs font-medium rounded-lg hover:bg-purple-700 transition-colors flex items-center gap-1"
                            >
                              <Play className="w-3 h-3" />
                              Continue Review
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <div className="px-6 py-3 border-t border-gray-200 flex items-center justify-between">
                  <div className="text-sm text-gray-700">
                    Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalItems} total)
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => handlePageChange(pagination.currentPage - 1)}
                      disabled={pagination.currentPage === 1}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() => handlePageChange(pagination.currentPage + 1)}
                      disabled={pagination.currentPage === pagination.totalPages}
                      className="px-3 py-1 border border-gray-300 rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                    >
                      Next
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* View Details Modal */}
      {showDetailsModal && selectedReview && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-lg font-semibold text-gray-900">Review Details</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <div className="space-y-6">
                {/* Manuscript Info */}
                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="text-sm font-medium text-[--asfi-blue] mb-1">{selectedReview.manuscriptId}</p>
                      <h4 className="text-lg font-semibold text-gray-900">{selectedReview.title}</h4>
                      <p className="text-sm text-gray-600 mt-1">
                        by {selectedReview.authors?.join(', ')}
                      </p>
                    </div>
                    <span className={`inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-full border ${getPriorityColor(selectedReview.priority)}`}>
                      {selectedReview.priority.charAt(0).toUpperCase() + selectedReview.priority.slice(1)} Priority
                    </span>
                  </div>
                  {selectedReview.isWomenInScience && (
                    <span className="inline-flex mt-2 items-center px-2 py-1 text-xs font-medium bg-pink-100 text-pink-800 rounded">
                      Women in Contemporary Science in Africa
                    </span>
                  )}
                  {selectedReview.isBelispointAcademic && (
                    <span className="inline-flex mt-2 items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded">
                      Belispoint Academic
                    </span>
                  )}
                  {selectedReview.isKidnappingForRansom && (
                    <span className="inline-flex mt-2 items-center px-2 py-1 text-xs font-medium bg-red-100 text-red-800 rounded">
                      Kidnapping For Ransom
                    </span>
                  )}
                </div>

                {/* Dates */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Invited Date</p>
                    <p className="text-lg font-semibold text-gray-900">{selectedReview.invitedDate}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Due Date</p>
                    <p className="text-lg font-semibold text-gray-900">{selectedReview.dueDate}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Days Left</p>
                    <p className={`text-lg font-semibold ${getDaysLeftColor(selectedReview.daysLeft)}`}>
                      {selectedReview.daysLeft} days
                    </p>
                  </div>
                </div>

                {/* Abstract */}
                {selectedReview.abstract && (
                  <div>
                    <h5 className="font-medium text-gray-900 mb-2">Abstract</h5>
                    <div className="bg-gray-50 p-4 rounded-lg prose max-w-none">
                      {/* <div dangerouslySetInnerHTML={{ 
                        __html: parseQuillContent(selectedReview.abstract) || 'No abstract available' 
                      }} /> */}
                      <RichTextViewer content={selectedReview.abstract} />
                    </div>
                  </div>
                )}

                {/* Files */}
                {selectedReview.files && Object.keys(selectedReview.files).length > 0 && (
                  <div>
                    <h5 className="font-medium text-gray-900 mb-3">Manuscript Files</h5>
                    <div className="space-y-2">
                      {Object.entries(selectedReview.files).map(([key, value]) => (
                        <div key={key} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div className="flex items-center">
                            <FileText className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-sm text-gray-700 capitalize">
                              {key.replace(/_/g, ' ')}:
                            </span>
                            <span className="ml-2 text-sm text-gray-600">{value}</span>
                          </div>
                          <button 
                            onClick={() => handleDownloadFile(value, key)}
                            className="p-1 text-gray-500 hover:text-[--asfi-blue] hover:bg-white rounded transition-colors"
                            title={`Download ${value}`}
                          >
                            <Download className="w-4 h-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div className="mt-8 flex justify-end gap-3">
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  onClick={() => {
                    setShowDetailsModal(false);
                    handleStartReview(selectedReview.manuscriptId);
                  }}
                  className="px-4 py-2 bg-[--asfi-blue] text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
                >
                  <Play className="w-4 h-4" />
                  Continue Review
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PendingReviews;